import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, of, tap } from 'rxjs';
import { Modal } from 'src/app/@domain/modal/modal.enum';
import { ModalService } from 'src/app/@domain/modal/modal.service';
import { UserService } from 'src/app/@domain/user/application/user.service';
import { NotificationService } from '../../../../../../@domain/notification/application/notification.service';
import { ModalComponent } from '../../../../global/modal/modal.component';
import { SpinnerComponent } from '../../../../global/spinner/spinner.component';
import { ImageDirective } from 'src/app/@common/DOM/image.directive';


@Component({
    selector: 'app-delete-account-modal',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [ ModalComponent, SpinnerComponent, ImageDirective ],
    styles: `
        @use "variables" as *;

        .modal-inner {
            display: flex;
            flex-direction: column;
            align-items: center;
            padding: 25px;
            text-align: center;
        }

        .bin-icon {
            width: 48px;
            height: 48px;
            margin-bottom: 15px;
            flex-shrink: 0;
        }

        h2 {
            font-size: 1.25rem;
            font-weight: 700;
            color: $red;
            text-transform: uppercase;
            letter-spacing: 0.04em;
            margin: 0 0 25px;
        }

        .description {
            font-size: 0.9375rem;
            color: $dark-blue;
            line-height: 1.6;
            margin: 0 0 25px;
            text-align: left;
            width: 100%;
        }

        .warning {
            font-size: 0.9375rem;
            color: $dark-blue;
            margin: 0 0 25px;
            text-align: left;
            width: 100%;
        }

        .actions {
            display: flex;
            gap: 25px;
            width: 100%;

            .btn {
                width: 165px;
                height: 40px;
                display: inline-flex;
                align-items: center;
                justify-content: center;
                font-size: 16px;
                font-weight: 700;
                letter-spacing: 0.06em;
                text-transform: uppercase;
                cursor: pointer;
                border-radius: 4px !important;
                border: none;

                &:disabled {
                    opacity: 0.6;
                    cursor: not-allowed;
                }
            }

            .btn-cancel {
                background: $light-blue;
                color: $dark-blue;
                &:hover { background: $input-bg; }
            }

            .btn-delete {
                background: $red;
                color: $white;
                &:hover:not(:disabled) { background: $dark-red; }
            }
        }
    `,
    template: `
        <app-modal [tag]="Modal.DeleteAccount" [isSmall]="true">
            <div class="modal-inner">
                <img class="bin-icon" src="assets/icons/bin.svg" alt="Delete" />

                <h2>Delete Account</h2>

                <p class="description">
                    This will permanently delete your user account,<br />
                    your email and username, all your playlists, and<br />
                    all your bookmarked videos.
                </p>

                <p class="warning">This action cannot be undone.</p>

                <div class="actions">
                    <button class="btn btn-cancel" type="button" (click)="close()">CANCEL</button>
                    <button class="btn btn-delete" type="button"
                            [disabled]="isLoading()"
                            (click)="deleteAccount()">
                        @if (isLoading()) {
                            <app-spinner />
                        } @else {
                            DELETE
                        }
                    </button>
                </div>
            </div>
        </app-modal>
    `
})
export class DeleteAccountModalComponent {
    protected readonly Modal = Modal;

    private modalService = inject(ModalService);
    private userService = inject(UserService);
    private notificationService = inject(NotificationService);
    private router = inject(Router);

    isLoading = signal(false);

    close(): void {
        this.modalService.close();
    }

    deleteAccount(): void {
        this.isLoading.set(true);
        this.userService.deleteAccount().pipe(
            tap(() => {
                this.modalService.close();
                this.notificationService.success({ detail: 'Account deleted successfully.' });
                this.router.navigate([ '/' ]);
            }),
            catchError(() => {
                this.notificationService.error({ detail: 'Failed to delete account. Please try again.' });
                return of(null);
            })
        ).subscribe(() => this.isLoading.set(false));
    }
}
